import React, { memo, useMemo } from 'react';
import { RichMarkdown } from '../markdown';
import ConsultantReport from '../consulting/ConsultantReport';
import { getBlockComponent } from './blockRegistry';
import ExpandableSectionBlock from './ExpandableSectionBlock';
import { MarkdownBlock, UnsupportedBlock } from './UnsupportedBlock';
import {
    buildStructuredBlocksCacheKey,
    normalizeResponseBlocks,
} from './structuredBlockUtils';
import styles from './structuredResponse.module.css';

function StructuredBlock({ block, renderContext }) {
    const Component = getBlockComponent(block.type);

    if (block.type === 'expandable_section') {
        return (
            <ExpandableSectionBlock
                block={block}
                renderContext={renderContext}
                renderNested={nestedBlocks =>
                    nestedBlocks.map((nested, index) => (
                        <StructuredBlock
                            key={nested.id || `${block.id}-nested-${index}`}
                            block={nested}
                            renderContext={renderContext}
                        />
                    ))
                }
            />
        );
    }

    if (!Component) {
        return <UnsupportedBlock block={block} renderContext={renderContext} />;
    }

    if (block.type === 'markdown') {
        return <MarkdownBlock block={block} renderContext={renderContext} />;
    }

    return <Component block={block} renderContext={renderContext} />;
}

const MemoStructuredBlock = memo(StructuredBlock);

function StructuredResponseRenderer({
    blocks,
    fallbackContent,
    citations = [],
    chunks = [],
    citationScope = '',
    onCitationClick,
    onViewEvidence,
    className = '',
    consultantLayout = true,
    reportActions = null,
}) {
    const normalizedBlocks = useMemo(() => normalizeResponseBlocks(blocks), [blocks]);

    const renderContext = useMemo(
        () => ({
            citations,
            chunks,
            citationScope,
            onCitationClick,
            onViewEvidence,
        }),
        [citations, chunks, citationScope, onCitationClick, onViewEvidence],
    );

    if (!normalizedBlocks.length) {
        return (
            <RichMarkdown
                content={fallbackContent}
                citations={citations}
                chunks={chunks}
                citationScope={citationScope}
                onCitationClick={onCitationClick}
                className={className}
            />
        );
    }

    if (consultantLayout) {
        return (
            <ConsultantReport
                blocks={normalizedBlocks}
                fallbackContent={fallbackContent}
                renderContext={renderContext}
                reportActions={reportActions}
                className={className}
            />
        );
    }

    return (
        <div
            className={`${styles.structuredResponse} ${className}`.trim()}
            role="document"
            aria-label="Structured response">
            {normalizedBlocks.map(block => (
                <MemoStructuredBlock key={block.id} block={block} renderContext={renderContext} />
            ))}
        </div>
    );
}

function structuredRendererPropsAreEqual(prev, next) {
    return (
        prev.fallbackContent === next.fallbackContent &&
        buildStructuredBlocksCacheKey(prev.blocks) === buildStructuredBlocksCacheKey(next.blocks) &&
        prev.citations === next.citations &&
        prev.chunks === next.chunks &&
        prev.citationScope === next.citationScope &&
        prev.onCitationClick === next.onCitationClick &&
        prev.onViewEvidence === next.onViewEvidence &&
        prev.className === next.className &&
        prev.consultantLayout === next.consultantLayout &&
        prev.reportActions === next.reportActions
    );
}

export default memo(StructuredResponseRenderer, structuredRendererPropsAreEqual);
